import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Wrapper from '@components/wrapper.styled';
import Image, { ImageProps } from '@components/image';
import generateId from '@helpers/generate-id';
import getLinkConfig from '@helpers/get-link-config';
import Text from '@components/text';
import { RichTextBlock } from 'prismic-reactjs';
import { PrimaryProps } from '../module/index';
import { Item } from '../module/item-props.type';
import {
  StyledFooter,
  SocialIconWrapper,
  SocialContainer,
  MainContainer,
  LogoContainer,
  Logo,
  ExternalLink,
  BottomContainer,
  StyledSpan,
  BottomLinksContainer,
  BottomLinks,
  StyledLink,
  SocialMedia,
  StyledText,
  MetaContainer,
  TopContainer,
  ContactContainer,
  ContactInfoColumn,
  ContactInfoGroup,
  ContactInfoList,
  ContactInfoListItem,
  BadgeColumn,
  BadgeItem,
} from './footer.styled';

const metaLinkIdGenerator = generateId();
const socialLinkIdGenerator = generateId();
const contactGroupIdGenerator = generateId();
const contactItemIdGenerator = generateId();
const badgeIdGenerator = generateId();

interface LinksType {
  link_label: string;
  link: {
    url: string;
    uid: string;
    target: string;
  };
}

interface SocialMediaType {
  image: ImageProps;
  link: {
    uid: string;
    url: string;
    target: string;
  };
}

interface FooterPageInterface {
  prismicGeneralConfig: {
    data: {
      legal_brand_name?: string;
      logo?: {
        alt: string;
        url: string;
        uid: string;
      };
      links: LinksType[];
      social_media: SocialMediaType[];
      body: {
        slice_type: string;
        primary: PrimaryProps;
        items: Item[];
      }[];
    };
  };
  prismicFooterConfig: {
    data: {
      touts?: {
        image?: ImageProps;
      }[];
      body?: {
        slice_type: 'contact_column';
        primary: {
          title?: {
            text?: string;
          };
        };
        items: {
          description?: {
            richText?: RichTextBlock[];
          };
        }[];
      }[];
    };
  };
}

const query = graphql`
  query FooterQuery {
    prismicGeneralConfig {
      data {
        legal_brand_name
        logo {
          alt
          url
        }
        links {
          link {
            uid
            url
            target
          }
          link_label
        }
        social_media {
          image {
            url
            alt
          }
          link {
            url
            target
          }
        }
        body {
          ... on PrismicGeneralConfigDataBodyLinks {
            slice_type
            primary {
              title {
                text
              }
            }
            items {
              link {
                uid
                url
              }
              link_label
            }
          }
        }
      }
    }
    prismicFooterConfig {
      data {
        touts {
          image {
            url
            alt
          }
        }
        body {
          ... on PrismicFooterConfigDataBodyContactColumn {
            slice_type
            primary {
              title {
                text
              }
            }
            items {
              description {
                richText
              }
            }
          }
        }
      }
    }
  }
`;

const Footer = (): JSX.Element => {
  const {
    prismicGeneralConfig: {
      data: { legal_brand_name, logo, links, social_media },
    },
    prismicFooterConfig: {
      data: { body: footerBody = [], touts = [] },
    },
  }: FooterPageInterface = useStaticQuery(query);
  const currentYear = new Date().getFullYear();
  const copyText = `© ${currentYear} ${
    legal_brand_name || 'tripmastersoftware.com'
  } All rights reserved.`;

  const hasRichText = (richText?: RichTextBlock[]): boolean =>
    Array.isArray(richText) && richText.length > 0;

  const contactColumnSlices = footerBody.filter((slice) => slice?.slice_type === 'contact_column');
  const hasContactGroups = contactColumnSlices.some((slice) => {
    const hasTitle = Boolean(slice.primary?.title?.text);
    const hasItems = slice.items?.some((item) => hasRichText(item.description?.richText));
    return hasTitle || hasItems;
  });

  const badgeTouts = touts.filter((tout) => tout?.image?.url);
  const hasBadges = badgeTouts.length > 0;
  const shouldRenderContactSection = hasContactGroups || hasBadges;

  return (
    <StyledFooter>
      <Wrapper>
        <TopContainer>
          <MetaContainer>
            <LogoContainer>
              {logo?.url && <Logo url={logo.url} alt={logo.alt} objectFit="contain" />}
            </LogoContainer>
          </MetaContainer>
          <MainContainer>
            {shouldRenderContactSection && (
              <ContactContainer>
                {hasContactGroups && (
                  <ContactInfoColumn>
                    {contactColumnSlices.map((slice) => {
                      const contactItems =
                        slice.items?.filter((item) => hasRichText(item.description?.richText)) ??
                        [];
                      const hasSliceTitle = Boolean(slice.primary?.title?.text);

                      if (!hasSliceTitle && contactItems.length === 0) {
                        return null;
                      }

                      const groupId = contactGroupIdGenerator.next().value;

                      return (
                        <ContactInfoGroup key={groupId}>
                          {hasSliceTitle && (
                            <Text
                              type="title"
                              tag="h4"
                              fontColor="secondary"
                              fontSize="body"
                              stringText={slice.primary?.title?.text}
                              margin="none"
                            />
                          )}
                          {contactItems.length > 0 && (
                            <ContactInfoList>
                              {contactItems.map((item) => (
                                <ContactInfoListItem key={contactItemIdGenerator.next().value}>
                                  <StyledText
                                    type="description"
                                    fontColor="muted"
                                    fontSize="small"
                                    richText={item.description?.richText}
                                  />
                                </ContactInfoListItem>
                              ))}
                            </ContactInfoList>
                          )}
                        </ContactInfoGroup>
                      );
                    })}
                  </ContactInfoColumn>
                )}
              </ContactContainer>
            )}
          </MainContainer>
        </TopContainer>
      </Wrapper>
      {hasBadges && (
        <Wrapper>
          <TopContainer>
            <BadgeColumn>
              {badgeTouts.map((tout) => {
                const image = tout?.image;

                if (!image?.url) {
                  return null;
                }

                return (
                  <BadgeItem key={badgeIdGenerator.next().value}>
                    <Image
                      url={image.url}
                      alt={image.alt || 'Footer badge'}
                      className="footer-badge-image"
                    />
                  </BadgeItem>
                );
              })}
            </BadgeColumn>
          </TopContainer>
        </Wrapper>
      )}
      <Wrapper style={{ zIndex: 0 }}>
        <BottomContainer>
          <StyledSpan>{copyText}</StyledSpan>
          <BottomLinksContainer>
            <BottomLinks>
              {links.map(({ link_label, link }) => {
                const linkConfig = getLinkConfig(link);

                return (
                  <React.Fragment key={metaLinkIdGenerator.next().value}>
                    {linkConfig.isExternal ? (
                      <ExternalLink href={linkConfig.url}>{link_label}</ExternalLink>
                    ) : (
                      <StyledLink to={linkConfig.url}>{link_label}</StyledLink>
                    )}
                  </React.Fragment>
                );
              })}
            </BottomLinks>
            <SocialContainer>
              <SocialIconWrapper>
                {social_media.map(({ image, link }) => {
                  if (!image?.url || !link?.url) {
                    return null;
                  }

                  return (
                    <SocialMedia
                      href={link?.url}
                      target={link?.target}
                      rel="noreferrer"
                      key={socialLinkIdGenerator.next().value}
                      title={
                        image?.alt !== undefined
                          ? `Follow us on ${image?.alt}`
                          : 'Follow us on social'
                      }
                    >
                      <Image url={image?.url} alt={image?.alt} />
                    </SocialMedia>
                  );
                })}
              </SocialIconWrapper>
            </SocialContainer>
          </BottomLinksContainer>
        </BottomContainer>
      </Wrapper>
    </StyledFooter>
  );
};

export default Footer;
